/**
 * Workflow definition contracts — code-registered, typed workflow blueprints.
 *
 * Definitions describe the shape of a workflow: its steps, transitions,
 * required permissions, and metadata. They are registered at startup by
 * calling `WorkflowDefinitionRegistry.register()`.
 */

// ─── Step kinds ───────────────────────────────────────────────────────────────

/**
 * Discriminator for the type of interaction a step requires.
 *
 * - `form`         — Structured user input (rendered as a form).
 * - `ai-task`      — AI-generated draft output (backend processing).
 * - `review`       — Manual refinement/approval of prior output.
 * - `action`       — Deterministic backend work (e.g. tenant creation).
 * - `confirmation` — Summary + confirm/cancel before side effects.
 * - `info`         — Read-only informational display.
 */
export type WorkflowStepKind = 'form' | 'ai-task' | 'review' | 'action' | 'confirmation' | 'info';

export const WORKFLOW_STEP_KINDS: readonly WorkflowStepKind[] = ['form', 'ai-task', 'review', 'action', 'confirmation', 'info'] as const;

// ─── Step definition ──────────────────────────────────────────────────────────

export interface WorkflowStepDefinition {
  /** Unique key within the workflow (e.g. 'tenant-basics') */
  readonly key: string;
  /** Step interaction kind */
  readonly kind: WorkflowStepKind;
  /** i18n key for the step label */
  readonly labelKey: string;
  /** i18n key for the step description (optional) */
  readonly descriptionKey?: string;
  /** Key of the next step, or `null` for the terminal step */
  readonly next: string | null;
  /** Whether this step can be skipped by the user */
  readonly optional?: boolean;
}

// ─── Workflow definition ──────────────────────────────────────────────────────

/**
 * A complete workflow blueprint. Registered in code, not stored in the DB.
 *
 * The `plane` field determines which database and API surface the workflow
 * operates against. In v1 only `'control'` is supported.
 */
export interface WorkflowDefinition {
  /** Unique workflow key (e.g. 'control.tenant-onboarding') */
  readonly key: string;
  /** Definition version — increment when step structure changes */
  readonly version: number;
  /** Which plane this workflow belongs to */
  readonly plane: 'control' | 'app';
  /** i18n key for the workflow title */
  readonly labelKey: string;
  /** i18n key for the workflow description */
  readonly descriptionKey?: string;
  /** Permission required to start this workflow */
  readonly startPermission: string;
  /** Key of the first step */
  readonly initialStep: string;
  /** Ordered list of step definitions */
  readonly steps: readonly WorkflowStepDefinition[];
}

/**
 * Lightweight summary returned when listing available workflows.
 */
export interface WorkflowDefinitionSummary {
  key: string;
  version: number;
  plane: 'control' | 'app';
  labelKey: string;
  descriptionKey?: string;
  startPermission: string;
  stepCount: number;
}
